import type { FC } from "react";
import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useSelector } from "react-redux";

import { fullMonthAndYear } from "@similarweb/pro-logic-components";

import { getSegmentOrDomainCellEnrichedAttributes } from "app/components/React/Table/cells/SegmentOrDomainCell";
import type {
    IPlayersTableWidget } from "app/components/React/TopSites/DomainsTable/TopPlayersTableOverview/PlayersTableWidget";
import {
    PlayersTableWidget,
} from "app/components/React/TopSites/DomainsTable/TopPlayersTableOverview/PlayersTableWidget";
import {
    getTableOptions,
    getUrlForTableRecord,
} from "app/components/React/TopSites/DomainsTable/TopPlayersTableOverview/PlayersWidgetsContainerConfig";
import { translate } from "app/filters/i18nFilter";
import { RECORDS_AMOUNT, THRESHOLD } from "app/pages/industry-analysis/players/rising-and-declining-players/const";
import type { Records } from "app/pages/industry-analysis/players/table/types";
import { DurationService } from "app/services/DurationService";
import { SegmentsUtils } from "app/services/segments/SegmentsUtils";

import { getColumnsConfig } from "./getColumnConfig";
import type { INoData } from "./types";

interface IPlayers {
    dataAutomationAttribute: string;
    titleKey: string;
    titleTooltipKey: string;
    records: Records;
    isLoading: boolean;
    isPeriodOverPeriod: boolean;
    isYearOverYear: boolean;
    playersTableWidgetProps?: Partial<IPlayersTableWidget>;
    noData?: INoData;
}
export const Players: FC<IPlayers> = (props) => {
    const {
        playersTableWidgetProps,
        dataAutomationAttribute,
        titleKey,
        titleTooltipKey,
        records,
        isLoading,
        isPeriodOverPeriod,
        isYearOverYear,
        noData = {},
    } = props;
    const { params } = useCurrentStateAndParams();
    const durationServices = new DurationService();
    const { duration } = params;
    const fullDuration = durationServices.getDurationData(duration);
    const date = fullDuration.raw.to.format(fullMonthAndYear);
    const columns = getColumnsConfig(isPeriodOverPeriod, isYearOverYear);

    const options = useMemo(() => {
        const tableOptions = getTableOptions();
        return {
            ...tableOptions,
            ...noData,
        };
    }, []);

    const tableConfig = { columns, options: options };

    const segmentsModule = useSelector((state) => {
        const { segmentsModule } = state;
        return segmentsModule;
    });
    const getUserAndAccountSegmentById = SegmentsUtils.getUserAndAccountSegmentByIdHof(segmentsModule);
    const { attribute, value } = THRESHOLD;
    const tableData = records
        .filter((record) => {
            const { [attribute]: thresholdAttribute } = record;
            return thresholdAttribute > value;
        })
        .slice(0, RECORDS_AMOUNT)
        .map((record) => {
            const { Id: segmentId, AvgUniqueUsers: LastMonthUniqueUsers } = record;
            const segment = getUserAndAccountSegmentById(segmentId);
            const segmentOrDomainCellEnrichedAttributes = getSegmentOrDomainCellEnrichedAttributes(record, segment);
            return {
                LastMonthUniqueUsers,
                ...record,
                ...segmentOrDomainCellEnrichedAttributes,
                url: getUrlForTableRecord(record),
            };
        });

    const formattedTableData = { Data: tableData };
    return (
        <PlayersTableWidget
            tableConfig={tableConfig}
            widgetTitle={translate(titleKey)}
            widgetTooltip={translate(titleTooltipKey)}
            formattedTableData={formattedTableData}
            dataAutomationAttribute={dataAutomationAttribute}
            date={date}
            loaderRowsNumber={RECORDS_AMOUNT}
            isLoading={isLoading}
            showFromOnHeader={true}
            {...playersTableWidgetProps}
        />
    );
};
